import { ld } from "@/lib/theme/light-dark";
import { ChevronDown, Ellipsis, Mail, Reply } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { HeroVorgangMessageBody } from "./hero-vorgang-message-body";
import {
  HERO_VORGANG_BUBBLE_INBOUND_BG,
  HERO_VORGANG_BUBBLE_OUTBOUND_BG,
  HERO_VORGANG_CHANNEL_HEADER_BG,
  HERO_VORGANG_CHANNEL_HEADER_BORDER,
} from "./hero-vorgang-tokens";

export type HeroVorgangMessageData = {
  readonly id: string;
  readonly direction: "inbound" | "outbound";
  readonly channel: "email" | "portal";
  readonly subject?: string;
  readonly fromLine?: string;
  readonly authorName: string;
  readonly authorInitials: string;
  readonly authorAvatarBg: string;
  readonly authorAvatarFg: string;
  readonly roleLabel?: string;
  readonly timestamp: string;
  readonly fullPlain: string;
  readonly signatureName?: string;
};

const MUTED: string = ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)");

function bubbleShellStyle(direction: "inbound" | "outbound"): CSSProperties {
  const isOutbound: boolean = direction === "outbound";
  return {
    width: "100%",
    maxWidth: isOutbound ? "68%" : "75%",
    alignSelf: isOutbound ? "flex-end" : "flex-start",
    borderRadius: 12,
    background: isOutbound
      ? HERO_VORGANG_BUBBLE_OUTBOUND_BG
      : HERO_VORGANG_BUBBLE_INBOUND_BG,
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    overflow: "hidden",
  };
}

/** Address part of "Name <mail>". */
function emailOf(fromLine: string | undefined): string | null {
  const match: RegExpMatchArray | null = fromLine?.match(/<([^>]+)>/) ?? null;
  return match?.[1] ?? null;
}

function EmailChannelHeader(props: {
  readonly subject: string;
  readonly fromLine: string;
  readonly isOutbound: boolean;
}): ReactNode {
  const t = useTranslations("marketing.heroVorgangMessage");
  return (
    <div
      style={{
        margin: "12px 12px 0",
        padding: "8px 12px",
        borderRadius: 6,
        border: `1px solid ${HERO_VORGANG_CHANNEL_HEADER_BORDER}`,
        background: HERO_VORGANG_CHANNEL_HEADER_BG,
        fontSize: 11,
        lineHeight: 1.45,
        color: ld("rgba(11,15,25,0.68)", "rgba(250,250,250,0.68)"),
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
          <span
            style={{
              color: ld("rgba(11,15,25,0.52)", "rgba(250,250,250,0.52)"),
              display: "inline-flex",
              marginTop: 1,
            }}
          >
            {props.isOutbound ? (
              <Reply width={14} height={14} strokeWidth={1.75} />
            ) : (
              <Mail width={14} height={14} strokeWidth={1.75} />
            )}
          </span>
          <span style={{ fontWeight: 600, color: ld("rgba(11,15,25,0.80)", "rgba(250,250,250,0.8)") }}>
            {props.subject}
          </span>
        </div>
        <div style={{ paddingLeft: 20, marginTop: 2 }}>
          <span style={{ color: MUTED }}>{t("von")} </span>
          <span style={{ color: ld("rgba(11,15,25,0.72)", "rgba(250,250,250,0.72)") }}>{props.fromLine}</span>
        </div>
      </div>
      <span style={{ color: MUTED, display: "inline-flex", flexShrink: 0 }}>
        <ChevronDown width={14} height={14} strokeWidth={1.75} />
      </span>
    </div>
  );
}

export function HeroVorgangMessage(props: {
  readonly message: HeroVorgangMessageData;
}): ReactNode {
  const msg: HeroVorgangMessageData = props.message;
  const isOutbound: boolean = msg.direction === "outbound";
  const isEmail: boolean = msg.channel === "email";
  const showEmailHeader: boolean =
    isEmail && msg.subject !== undefined && msg.fromLine !== undefined;
  const address: string | null = isEmail ? emailOf(msg.fromLine) : null;

  return (
    <div style={bubbleShellStyle(msg.direction)}>
      {showEmailHeader ? (
        <EmailChannelHeader
          subject={msg.subject ?? ""}
          fromLine={msg.fromLine ?? ""}
          isOutbound={isOutbound}
        />
      ) : null}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 16px 0",
          minWidth: 0,
        }}
      >
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: msg.authorAvatarBg,
            color: msg.authorAvatarFg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {msg.authorInitials}
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: ld("#0B0F19", "#fafafa"),
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {msg.authorName}
        </span>
        {address !== null || msg.roleLabel !== undefined ? (
          <span
            style={{
              fontSize: 11.5,
              color: MUTED,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {address ?? msg.roleLabel}
          </span>
        ) : null}
        <span style={{ fontSize: 11.5, color: MUTED, whiteSpace: "nowrap", flexShrink: 0 }}>
          {msg.timestamp}
        </span>
        <span
          style={{
            width: 4,
            height: 4,
            borderRadius: "50%",
            background: "#3A9E6B",
            flexShrink: 0,
          }}
        />
        <span
          style={{
            marginLeft: "auto",
            color: MUTED,
            display: "inline-flex",
            flexShrink: 0,
          }}
        >
          <Ellipsis width={14} height={14} strokeWidth={1.75} />
        </span>
      </div>

      <HeroVorgangMessageBody
        channel={msg.channel}
        direction={msg.direction}
        fullPlain={msg.fullPlain}
        signatureName={msg.signatureName}
      />
    </div>
  );
}
